// Iframe bridge — běží UVNITŘ iframe s viewerem (§21B). Nad postMessage staví
// stejné logické API jako přímé SDK: přijímá volání metod od rodiče, vrací
// výsledky a přeposílá události. Origin se kontroluje (v produkci nikdy "*").
import { IFCViewer } from "./IFCViewer.js";
import { ViewerEvents } from "./events.js";
import { BRIDGE_PROTOCOL, SDK_VERSION } from "./version.js";
import { ViewerError } from "./errors.js";

const TAG = "__ifcsdk";

// options: { container, allowedOrigins: string[]|"*", ...IFCViewerOptions }
export function startViewerBridge(options = {}) {
  const allowed = options.allowedOrigins || [];
  const allowAll = allowed === "*" || (Array.isArray(allowed) && allowed.includes("*"));
  const viewer = new IFCViewer(options);
  let parentOrigin = null;

  const originOk = (origin) => allowAll || (Array.isArray(allowed) && allowed.includes(origin));

  const post = (msg, origin) => {
    const target = origin || parentOrigin || (allowAll ? "*" : null);
    if (target == null) return; // bez známého originu neposíláme (bezpečnost)
    try { window.parent.postMessage({ [TAG]: BRIDGE_PROTOCOL, ...msg }, target); } catch (_) {}
  };

  // „ready" musíme umět odeslat i dřív, než přijde první zpráva od rodiče (jinak by
  // vznikl deadlock: rodič čeká na ready, bridge čeká na zprávu kvůli originu).
  // Rozešleme ho na všechny KONKRÉTNÍ povolené originy (nikdy paušálně "*").
  const postReadyBroadcast = (msg) => {
    if (parentOrigin) { post(msg, parentOrigin); return; }
    if (Array.isArray(allowed)) for (const o of allowed) if (o && o !== "*") post(msg, o);
  };

  // Přeposílání všech veřejných událostí rodiči.
  for (const ev of ViewerEvents) {
    viewer.on(ev, (payload) => post({ type: "event", event: ev, payload }));
  }

  // Rozřeší tečkovou cestu metody (např. "sections.create").
  const resolveMethod = (name) => {
    const parts = String(name).split(".");
    let ctx = viewer, fn = viewer;
    for (const p of parts) { ctx = fn; fn = fn && fn[p]; }
    return { fn, ctx };
  };

  window.addEventListener("message", async (e) => {
    const data = e.data;
    if (!data || data[TAG] !== BRIDGE_PROTOCOL) return;
    if (!originOk(e.origin)) return;            // odmítnout cizí origin
    if (!parentOrigin) parentOrigin = e.origin; // zapamatovat pro odpovědi

    if (data.type === "connect") { post({ type: "ready", version: SDK_VERSION, protocol: BRIDGE_PROTOCOL, ready: bridgeReady }, e.origin); return; }
    if (data.type === "call") {
      const { id, method, args } = data;
      try {
        const { fn, ctx } = resolveMethod(method);
        if (typeof fn !== "function") throw new ViewerError("INVALID_ARGUMENT", "Neznámá metoda: " + method);
        const value = await fn.apply(ctx, args || []);
        post({ type: "result", id, ok: true, value: serialize(value) }, e.origin);
      } catch (err) {
        const payload = err instanceof ViewerError ? err.toJSON() : { code: "INTERNAL", message: String(err && err.message || err), details: {} };
        post({ type: "result", id, ok: false, error: payload }, e.origin);
      }
    }
  });

  // Inicializace viewer jádra a oznámení připravenosti (broadcast na povolené
  // originy — rodič nemusel ještě nic poslat).
  let bridgeReady = false;
  viewer.init()
    .then(() => { bridgeReady = true; postReadyBroadcast({ type: "ready", version: SDK_VERSION, protocol: BRIDGE_PROTOCOL, ready: true }); })
    .catch((err) => postReadyBroadcast({ type: "ready", version: SDK_VERSION, protocol: BRIDGE_PROTOCOL, error: String(err && err.message || err) }));

  return viewer;
}

// Ořízne nepřenositelné hodnoty (postMessage struktury musí být klonovatelné).
function serialize(v) {
  if (v === undefined) return null;
  try { return JSON.parse(JSON.stringify(v)); } catch (_) { return null; }
}
